// ONDEMAND-1 (docs/specs/ondemand-1.md) in-memory fixtures for `dev:mock`: the feature is ENABLED
// here so transcribe-on-demand can be demoed with no backend. Everything is derived lazily from
// lib/mock.ts's `mockRecordings` (never read at module load — App.test.tsx stubs `./mock` with a
// partial module, and a top-level read would crash its import).
//
// A "transcription" here is a timer: ensure flips a deferred row to `transcribing`, then to
// `completed` a few seconds later (the mock transcript is one fixed fixture, so it renders
// whatever was opened). Rows are replaced, never mutated in place, so React Query's structural
// sharing sees the change.
import type {
  BatchTranscribeResponse,
  CreateTranscriptionSubscriptionRequest,
  Recording,
  SummaryAccessState,
  TranscriptionPolicy,
  TranscriptionSettings,
  TranscriptionStatus,
  TranscriptionSubscription,
  TranscriptionSubscriptionList,
  UntranscribedSearchResult,
  UpdateTranscriptionSettingsRequest,
} from '@scribe/core/types';
import { isDeferred } from '@scribe/core/types';
import { mockRecordings, mockSummaryArtifacts, mockTags } from './mock';

const MOCK_TRANSCRIBE_MS = 8_000;
const MOCK_SUMMARIZE_MS = 6_000;
/** List-price estimate per audio minute — mock only; the real figure comes from the server. */
const MOCK_USD_PER_MIN = 0.024;
const MAX_BATCH = 10;

const settings: TranscriptionSettings = {
  feature_enabled: true,
  org_default_policy: 'immediate',
  user_default_policy: null,
  effective_default_policy: 'immediate',
  effective_default_source: 'org_default',
  webhook_subscriber: false,
};

let subSeq = 1;
const subscriptions: TranscriptionSubscription[] = [
  {
    id: 'sub-1',
    user_id: 'user-1',
    kind: 'tag',
    tag_name: 'client-call',
    is_active: true,
    created_at: new Date().toISOString(),
    updated_at: new Date().toISOString(),
  },
];

/** Recordings whose summary pass this mock has started but not yet "finished". */
const summarizing = new Set<string>();

function findIndex(id: string): number {
  return mockRecordings.findIndex((r) => r.id === id);
}

function patchRecording(id: string, patch: Partial<Recording>): void {
  const i = findIndex(id);
  if (i >= 0)
    mockRecordings[i] = { ...mockRecordings[i]!, ...patch, updated_at: new Date().toISOString() };
}

function etaSeconds(r: Recording): number {
  return Math.max(60, Math.round((r.estimated_duration_sec ?? 600) / 6));
}

function aboutMinutes(sec: number): string {
  return `about ${Math.max(1, Math.round(sec / 60))} min`;
}

function summaryState(r: Recording): SummaryAccessState {
  if (r.status !== 'completed') return 'not_applicable';
  if ((mockSummaryArtifacts.get(r.id) ?? []).length > 0) return 'ready';
  return summarizing.has(r.id) ? 'summarizing' : 'pending';
}

function explanation(r: Recording): TranscriptionStatus['auto_transcribe'] {
  if (r.transcription_policy === 'immediate') {
    return {
      policy: 'immediate',
      source: 'recording_override',
      reason: 'This recording is set to transcribe automatically.',
      subscription_ids: [],
    };
  }
  if (r.transcription_policy === 'on_access') {
    return {
      policy: 'on_access',
      source: 'recording_override',
      reason: 'This recording is set to transcribe only when opened, searched or asked about.',
      subscription_ids: [],
    };
  }
  return {
    policy: settings.effective_default_policy ?? 'on_access',
    source: settings.effective_default_source ?? 'org_default',
    reason:
      settings.effective_default_policy === 'immediate'
        ? 'Your default is to transcribe recordings automatically.'
        : "Your organization's default is to transcribe recordings when opened, searched or asked about.",
    subscription_ids: [],
  };
}

function statusOf(r: Recording, triggered = false, summaryTriggered = false): TranscriptionStatus {
  const base = {
    recording_id: r.id,
    triggered,
    summary_triggered: summaryTriggered,
    summary_state: summaryState(r),
    estimated_duration_sec: r.estimated_duration_sec ?? null,
    auto_transcribe: explanation(r),
  };
  if (isDeferred(r)) {
    return { ...base, state: 'deferred', message: 'Not transcribed yet.', eta_seconds: null };
  }
  if (r.status === 'queued' || r.status === 'transcribing') {
    const eta = etaSeconds(r);
    return {
      ...base,
      state: 'in_progress',
      message: `Transcribing now — ${aboutMinutes(eta)}.`,
      eta_seconds: eta,
    };
  }
  if (r.status === 'failed')
    return { ...base, state: 'failed', message: 'Transcription failed.', eta_seconds: null };
  if (r.status === 'completed')
    return { ...base, state: 'completed', message: 'Transcribed.', eta_seconds: null };
  return {
    ...base,
    state: 'processing',
    message: 'Audio is still being processed.',
    eta_seconds: null,
  };
}

function startTranscription(id: string, trigger: string): void {
  patchRecording(id, {
    status: 'transcribing',
    transcription_deferred_at: null,
    transcription_trigger: trigger,
    transcription_started_at: new Date().toISOString(),
  });
  setTimeout(() => {
    const r = mockRecordings[findIndex(id)];
    patchRecording(id, {
      status: 'completed',
      duration_sec: r?.estimated_duration_sec ?? null,
      transcription_completed_at: new Date().toISOString(),
    });
  }, MOCK_TRANSCRIBE_MS);
}

function startSummary(r: Recording): void {
  summarizing.add(r.id);
  setTimeout(() => {
    summarizing.delete(r.id);
    mockSummaryArtifacts.set(r.id, [
      {
        id: `artifact-summary-${r.id}`,
        recording_id: r.id,
        kind: 'summary',
        content: {
          summary: 'A placeholder summary generated the first time this recording was opened.',
          title: r.title,
          key_points: ['Placeholder key point one.', 'Placeholder key point two.'],
          action_items: [],
          topics: ['placeholder'],
        },
        model_id: 'mock-summarizer',
        created_at: new Date().toISOString(),
      },
    ]);
  }, MOCK_SUMMARIZE_MS);
}

function unavailable(id: string): TranscriptionStatus {
  return {
    recording_id: id,
    state: 'unavailable',
    triggered: false,
    message: 'Recording not found.',
    summary_state: 'not_applicable',
    summary_triggered: false,
    auto_transcribe: explanation({ transcription_policy: null } as Recording),
  };
}

export const mockOndemand = {
  getSettings: (): TranscriptionSettings => ({ ...settings }),
  updateSettings: (body: UpdateTranscriptionSettingsRequest): TranscriptionSettings => {
    settings.user_default_policy = body.user_default_policy;
    settings.effective_default_policy =
      body.user_default_policy ?? settings.org_default_policy ?? null;
    settings.effective_default_source = body.user_default_policy ? 'user_default' : 'org_default';
    return { ...settings };
  },
  listSubscriptions: (): TranscriptionSubscriptionList => ({
    items: subscriptions.map((s) => ({ ...s })),
    implied_by_webhook: settings.webhook_subscriber === true,
  }),
  createSubscription: (body: CreateTranscriptionSubscriptionRequest): TranscriptionSubscription => {
    const ts = new Date().toISOString();
    const sub: TranscriptionSubscription = {
      id: `sub-${++subSeq}`,
      user_id: 'user-1',
      kind: body.kind,
      folder_id: body.folder_id ?? null,
      tag_name: body.tag_name ?? null,
      title_pattern: body.title_pattern ?? null,
      is_active: true,
      created_at: ts,
      updated_at: ts,
    };
    subscriptions.push(sub);
    return { ...sub };
  },
  updateSubscription: (id: string, isActive: boolean): TranscriptionSubscription => {
    const sub = subscriptions.find((s) => s.id === id);
    if (!sub) throw new Error('Subscription not found.');
    sub.is_active = isActive;
    sub.updated_at = new Date().toISOString();
    return { ...sub };
  },
  deleteSubscription: (id: string): void => {
    const i = subscriptions.findIndex((s) => s.id === id);
    if (i >= 0) subscriptions.splice(i, 1);
  },
  getStatus: (id: string): TranscriptionStatus => {
    const r = mockRecordings[findIndex(id)];
    return r ? statusOf(r) : unavailable(id);
  },
  ensure: (id: string, trigger = 'opened'): TranscriptionStatus => {
    const r = mockRecordings[findIndex(id)];
    if (!r) return unavailable(id);
    if (isDeferred(r)) {
      startTranscription(id, trigger);
      return statusOf(mockRecordings[findIndex(id)]!, true);
    }
    if (summaryState(r) === 'pending' && r.job_spec?.auto_summary !== false) {
      startSummary(r);
      return statusOf(r, false, true);
    }
    return statusOf(r);
  },
  setPolicy: (id: string, policy: TranscriptionPolicy | null): TranscriptionStatus => {
    patchRecording(id, { transcription_policy: policy });
    return mockOndemand.getStatus(id);
  },
  searchUntranscribed: (q: string): UntranscribedSearchResult => {
    const needle = q.trim().toLowerCase();
    const tagMatch = mockTags.some((t) => t.name.toLowerCase().includes(needle));
    const items = needle
      ? mockRecordings
          .filter(isDeferred)
          .filter((r) => tagMatch || r.title.toLowerCase().includes(needle))
          .map((r) => ({
            recording_id: r.id,
            title: r.title,
            owner_id: r.owner_id,
            created_at: r.created_at,
            estimated_duration_sec: r.estimated_duration_sec ?? null,
            matched_on: [tagMatch ? ('tag' as const) : ('title' as const)],
          }))
      : [];
    const first = items.slice(0, MAX_BATCH);
    const minutes = Math.round(first.reduce((n, m) => n + (m.estimated_duration_sec ?? 0), 0) / 60);
    return {
      total: items.length,
      items,
      estimated_minutes: minutes,
      estimated_cost_usd: Math.round(minutes * MOCK_USD_PER_MIN * 100) / 100,
      max_batch: MAX_BATCH,
    };
  },
  transcribeBatch: (ids: string[]): BatchTranscribeResponse => {
    let minutes = 0;
    const items = ids.map((id) => {
      const r = mockRecordings[findIndex(id)];
      if (!r)
        return { recording_id: id, outcome: 'not_found' as const, message: 'Recording not found.' };
      if (!isDeferred(r)) {
        return {
          recording_id: id,
          outcome: 'already_transcribed' as const,
          message: 'Already transcribed.',
        };
      }
      minutes += (r.estimated_duration_sec ?? 0) / 60;
      const eta = etaSeconds(r);
      startTranscription(id, 'searched');
      return {
        recording_id: id,
        outcome: 'triggered' as const,
        message: `Transcribing now — ${aboutMinutes(eta)}.`,
        eta_seconds: eta,
      };
    });
    return {
      items,
      triggered_count: items.filter((i) => i.outcome === 'triggered').length,
      estimated_cost_usd: Math.round(minutes * MOCK_USD_PER_MIN * 100) / 100,
    };
  },
};
